import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Globe, 
  LogOut, 
  Send, 
  ArrowLeft, 
  Bot, 
  User, 
  Mic, 
  MicOff,
  CreditCard,
  Wallet,
  Loader2,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useWallet } from '@/contexts/WalletContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedChatInterface = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const wallet = useWallet();
  
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: "👋 Hi! I'm your RouteFlow AI assistant. I'll help you send money globally!\n\nYou can type or speak to me. Try saying:\n• 'Send $200 to Mexico'\n• 'I want to send money to Philippines'\n• 'Transfer €500 to Nigeria'\n\nHow can I help you today?",
      type: 'text'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const [isListening, setIsListening] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);
  const [processingTransfer, setProcessingTransfer] = useState(false);
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
        // Auto-send after voice input
        handleSend(null, transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'no-speech') {
          toast.error('No speech detected. Please try again.');
        } else {
          toast.error('Voice recognition failed. Please try typing instead.');
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) {
      toast.error('Voice input not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        toast.info('Listening... Speak now!');
      } catch (error) {
        console.error('Error starting recognition:', error);
        toast.error('Could not start voice input');
      }
    }
  };

  const parseTransferIntent = (userMessage) => {
    // Simple intent parsing - extract amount, destination, currency
    const amountMatch = userMessage.match(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)|€(\d+(?:,\d{3})*(?:\.\d{2})?)|(\d+(?:,\d{3})*(?:\.\d{2})?)\s*(dollars?|euros?|USD|EUR)/i);
    const amount = amountMatch ? parseFloat(amountMatch[0].replace(/[$€,]/g, '').replace(/\s*(dollars?|euros?|USD|EUR)/i, '')) : null;

    const destinations = {
      'mexico': { country: 'Mexico', corridor: 'us-mx', currency: 'MXN' },
      'philippine': { country: 'Philippines', corridor: 'us-ph', currency: 'PHP' },
      'philippines': { country: 'Philippines', corridor: 'us-ph', currency: 'PHP' },
      'nigeria': { country: 'Nigeria', corridor: 'eu-ng', currency: 'NGN' }
    };

    let destination = null;
    for (const [key, value] of Object.entries(destinations)) {
      if (userMessage.toLowerCase().includes(key)) {
        destination = value;
        break;
      }
    }

    return { amount, destination };
  };

  const handleSend = async (e, voiceInput = null) => {
    if (e) e.preventDefault();
    
    const messageToSend = voiceInput || input.trim();
    if (!messageToSend || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: messageToSend, type: 'text' }]);
    setLoading(true);

    try {
      // Parse intent
      const intent = parseTransferIntent(messageToSend);

      // Send to AI for response
      const response = await axios.post(`${API}/chat`, {
        message: messageToSend,
        session_id: sessionId,
        user_id: user.id
      });

      // Check if this is a transfer request
      if (intent.amount && intent.destination) {
        // Store transfer data
        setTransferData({
          amount: intent.amount,
          destination: intent.destination,
          originalMessage: messageToSend
        });

        // Show AI confirmation and payment choice
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Great! I understand you want to send $${intent.amount} to ${intent.destination.country}.\n\nLet me help you with that. How would you like to pay?`,
          type: 'text'
        }]);

        setShowPaymentChoice(true);
      } else {
        // Regular AI response
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.response,
          type: 'text'
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to process your request. Please try again.');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I encountered an error. Could you please rephrase your request?",
        type: 'text'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentChoice = async (method) => {
    setShowPaymentChoice(false);

    if (method === 'crypto') {
      // Check if wallet is connected
      if (!wallet.isConnected) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "To send crypto, you'll need to connect your wallet first. Let me help you with that.",
          type: 'text'
        }]);
        
        try {
          await wallet.connectWallet();
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: "Great! Your wallet is connected. Now redirecting you to complete your transfer...",
            type: 'text'
          }]);
          
          setTimeout(() => {
            navigate('/crypto-transfer', { 
              state: { 
                amount: transferData.amount,
                destination: transferData.destination
              }
            });
          }, 2000);
        } catch (error) {
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: "I couldn't connect your wallet. Would you like to try again or use a different payment method?",
            type: 'text'
          }]);
        }
      } else {
        // Wallet already connected
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Perfect! Your wallet is connected. I'm preparing your transfer of $${transferData.amount} to ${transferData.destination.country}...`,
          type: 'text'
        }]);

        setTimeout(() => {
          navigate('/crypto-transfer', { 
            state: { 
              amount: transferData.amount,
              destination: transferData.destination
            }
          });
        }, 2000);
      }
    } else {
      // Fiat payment
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "🎉 Great choice! Fiat payments with credit cards and bank transfers are coming very soon!\n\nWe're currently integrating with payment providers to offer you:\n• Credit card payments\n• Bank transfers\n• Local payment methods\n\nIn the meantime, would you like to try our crypto payment option? It's instant and has very low fees!",
        type: 'text'
      }]);

      // Show option to switch to crypto
      setTimeout(() => {
        setShowPaymentChoice(true);
      }, 2000);
    }
  };

  const PaymentChoiceButtons = () => (
    <div className='flex flex-col gap-3 my-4'>
      <Button
        onClick={() => handlePaymentChoice('crypto')}
        className='w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 h-auto py-4'
      >
        <div className='flex items-center justify-between w-full'>
          <div className='flex items-center space-x-3'>
            <Wallet className='w-6 h-6' />
            <div className='text-left'>
              <p className='font-semibold'>Pay with Crypto</p>
              <p className='text-xs text-indigo-100'>Fast, low fees (~$0.50)</p>
            </div>
          </div>
          <CheckCircle className='w-5 h-5 text-green-300' />
        </div>
      </Button>

      <Button
        onClick={() => handlePaymentChoice('fiat')}
        variant='outline'
        className='w-full h-auto py-4 border-2'
      >
        <div className='flex items-center justify-between w-full'>
          <div className='flex items-center space-x-3'>
            <CreditCard className='w-6 h-6' />
            <div className='text-left'>
              <p className='font-semibold'>Pay with Card/Bank</p>
              <p className='text-xs text-gray-600'>Coming soon!</p>
            </div>
          </div>
          <span className='text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded'>Soon</span>
        </div>
      </Button>
    </div>
  );

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-4'>
              <Button variant='ghost' size='icon' onClick={() => navigate('/dashboard')}>
                <ArrowLeft className='w-5 h-5' />
              </Button>
              <div className='flex items-center space-x-3'>
                <img 
                  src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                  alt='RouteFlow AI' 
                  className='h-10 w-auto'
                />
                <div>
                  <span className='text-xl font-bold gradient-text'>AI Assistant</span>
                  <p className='text-xs text-gray-500'>Speak or type your request</p>
                </div>
              </div>
            </div>
            <Button variant='ghost' onClick={onLogout}>
              <LogOut className='w-4 h-4 mr-2' /> Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Chat Container */}
      <div className='max-w-4xl mx-auto px-4 py-6 h-[calc(100vh-5rem)] flex flex-col'>
        {/* Messages Area */}
        <Card className='flex-1 overflow-hidden flex flex-col border-0 shadow-lg mb-4'>
          <CardContent className='flex-1 overflow-y-auto p-6 space-y-4'>
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-start space-x-2 max-w-[80%] ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user' 
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-600' 
                      : 'bg-gradient-to-br from-emerald-500 to-teal-600'
                  }`}>
                    {message.role === 'user' ? <User className='w-5 h-5 text-white' /> : <Bot className='w-5 h-5 text-white' />}
                  </div>
                  <div className={`rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                      : 'bg-white border border-gray-200'
                  }`}>
                    <p className='text-sm whitespace-pre-wrap'>{message.content}</p>
                  </div>
                </div>
              </div>
            ))}

            {showPaymentChoice && <PaymentChoiceButtons />}

            {loading && (
              <div className='flex justify-start'>
                <div className='flex items-start space-x-2'>
                  <div className='w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center'>
                    <Bot className='w-5 h-5 text-white' />
                  </div>
                  <div className='bg-white border border-gray-200 rounded-2xl px-4 py-3'>
                    <div className='flex space-x-2'>
                      <div className='w-2 h-2 bg-emerald-500 rounded-full animate-bounce' style={{ animationDelay: '0ms' }}></div>
                      <div className='w-2 h-2 bg-emerald-500 rounded-full animate-bounce' style={{ animationDelay: '150ms' }}></div>
                      <div className='w-2 h-2 bg-emerald-500 rounded-full animate-bounce' style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </CardContent>
        </Card>

        {/* Input Area */}
        <Card className='border-0 shadow-lg'>
          <CardContent className='p-4'>
            <form onSubmit={handleSend} className='flex space-x-2'>
              <Button
                type='button'
                variant={isListening ? 'default' : 'outline'}
                size='icon'
                onClick={toggleVoiceInput}
                className={isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}
              >
                {isListening ? <MicOff className='w-5 h-5' /> : <Mic className='w-5 h-5' />}
              </Button>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isListening ? 'Listening...' : 'Type or speak your request...'}
                disabled={loading || isListening}
                className='flex-1'
              />
              <Button 
                type='submit' 
                disabled={!input.trim() || loading}
                className='bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
              >
                {loading ? <Loader2 className='w-5 h-5 animate-spin' /> : <Send className='w-5 h-5' />}
              </Button>
            </form>
            
            <div className='flex items-center justify-between mt-3 text-xs text-gray-500'>
              <p>💡 Try: "Send $200 to Mexico" or click the mic to speak</p>
              {wallet.isConnected && (
                <p className='text-emerald-600 font-semibold'>✓ Wallet Connected</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnhancedChatInterface;
